import React, { useState } from 'react';
import {
  TrendingUp,
  DollarSign,
  Users,
  Building2,
  FileText,
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
  ArrowUpRight,
  Sparkles,
  PieChart,
  BarChart3,
  Sliders,
  Award,
  Lock,
  RefreshCw,
  Plus,
  Send,
  Zap,
  Globe,
  Tag,
  Clock,
  Briefcase,
  HelpCircle,
  Filter,
  Search,
  Eye,
  Check,
  X,
  Layers,
  FileSpreadsheet
} from 'lucide-react';
import { AppView, PipelineStage, LeadStatus, DisqualificationReason, ClosedLostReason } from '../../types';
import { salesGtmEngine } from '../../services/sales/salesGtmEngine';

interface ChiefRevenueOfficerCommandCenterProps {
  onNavigateToView: (view: AppView) => void;
}

export const ChiefRevenueOfficerCommandCenter: React.FC<ChiefRevenueOfficerCommandCenterProps> = ({
  onNavigateToView,
}) => {
  const [activeTab, setActiveTab] = useState<
    | 'overview'
    | 'pipeline'
    | 'leads'
    | 'accounts'
    | 'quotes'
    | 'dealdesk'
    | 'forecast'
    | 'partners'
    | 'expansion'
    | 'copilot'
  >('overview');

  // Engine Data States
  const [leads, setLeads] = useState(salesGtmEngine.getLeads());
  const [accounts, setAccounts] = useState(salesGtmEngine.getAccounts());
  const [contacts] = useState(salesGtmEngine.getContacts());
  const [opportunities, setOpportunities] = useState(salesGtmEngine.getOpportunities());
  const [quotes, setQuotes] = useState(salesGtmEngine.getQuotes());
  const [dealDeskRequests, setDealDeskRequests] = useState(salesGtmEngine.getDealDeskRequests());
  const [forecastSnapshots] = useState(salesGtmEngine.getForecastSnapshots());
  const [partners] = useState(salesGtmEngine.getPartners());
  const [expansionSignals] = useState(salesGtmEngine.getExpansionSignals());
  const [renewals] = useState(salesGtmEngine.getRenewalOpportunities());
  const [contracts] = useState(salesGtmEngine.getContracts());
  const [dataQualityIssues] = useState(salesGtmEngine.getCRMDataQualityIssues());

  const paidMonetizationMode = salesGtmEngine.getPaidMonetizationMode();
  const securityGate = salesGtmEngine.checkSecurityGate();

  // Selected states for modals / detail views
  const [selectedOppId, setSelectedOppId] = useState<string>(opportunities[0]?.id || '');
  const [selectedAccountId, setSelectedAccountId] = useState<string>(accounts[0]?.id || '');
  const [searchFilter, setSearchFilter] = useState('');
  const [stageFilter, setStageFilter] = useState<string>('ALL');

  // New Lead Modal State
  const [isNewLeadModalOpen, setIsNewLeadModalOpen] = useState(false);
  const [newLeadForm, setNewLeadForm] = useState({
    companyName: '',
    contactName: '',
    businessEmail: '',
    jobTitle: '',
    market: 'FR',
    country: 'France',
    language: 'fr',
    productInterest: 'Enterprise Financial APIs & FX Engine',
    useCase: 'Integration of real-time salary & tax intelligence API',
    source: 'INBOUND_DEMO_REQUEST' as const,
    owner: 'alexandre.dubois@yamoopay.com',
    marketingEligibility: true,
  });

  // Quote Generator Modal State
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [quoteForm, setQuoteForm] = useState({
    accountId: accounts[0]?.id || '',
    opportunityId: opportunities[0]?.id || '',
    currency: 'USD',
    productName: 'Enterprise FX & Money Intelligence API Suite',
    sku: 'YP-API-ENT-FX-01',
    quantity: 12,
    unitPrice: 15000,
    discountType: 'PERCENTAGE' as const,
    discountValue: 10,
    paymentTerms: 'NET_30_DAYS',
  });

  // Action Messages
  const [notificationMsg, setNotificationMsg] = useState<string | null>(null);

  const triggerNotification = (msg: string) => {
    setNotificationMsg(msg);
    setTimeout(() => setNotificationMsg(null), 4000);
  };

  // KPI Computations
  const totalPipelineUsd = opportunities.reduce((acc, o) => acc + o.estimatedValue, 0);
  const weightedPipelineUsd = opportunities.reduce((acc, o) => acc + (o.estimatedValue * o.probabilityPct) / 100, 0);
  const commitPipelineUsd = opportunities
    .filter((o) => o.forecastCategory === 'COMMIT' || o.forecastCategory === 'CLOSED')
    .reduce((acc, o) => acc + o.estimatedValue, 0);
  const closedWonUsd = opportunities
    .filter((o) => o.stage === 'CLOSED_WON')
    .reduce((acc, o) => acc + o.estimatedValue, 0);
  const winRatePct = 78.4;
  const renewalPipelineUsd = renewals.reduce((acc, r) => acc + r.targetRenewalArrUsd, 0);

  // Handlers
  const handleToggleMonetizationMode = () => {
    const nextMode = paidMonetizationMode === 'OFF' ? 'INTERNAL_TEST' : paidMonetizationMode === 'INTERNAL_TEST' ? 'ON' : 'OFF';
    salesGtmEngine.setPaidMonetizationMode(nextMode);
    triggerNotification(`PAID_MONETIZATION_MODE toggled to ${nextMode}`);
  };

  const handleCreateLead = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newLeadForm.companyName || !newLeadForm.businessEmail) return;

    const created = salesGtmEngine.createLead(newLeadForm);
    setLeads([...salesGtmEngine.getLeads()]);
    setIsNewLeadModalOpen(false);
    triggerNotification(`Lead "${created.companyName}" registered with total score ${created.score.totalScore}/100 (${created.qualificationState})`);
  };

  const handleConvertLead = (leadId: string) => {
    try {
      const res = salesGtmEngine.convertLeadToAccount(leadId);
      setLeads([...salesGtmEngine.getLeads()]);
      setAccounts([...salesGtmEngine.getAccounts()]);
      setOpportunities([...salesGtmEngine.getOpportunities()]);
      triggerNotification(`Lead converted into Account "${res.account.name}" & Opportunity "${res.opportunity?.name}"`);
    } catch (err: any) {
      triggerNotification(`Error: ${err.message}`);
    }
  };

  const handleUpdateOpportunityStage = (oppId: string, newStage: PipelineStage) => {
    try {
      salesGtmEngine.updateOpportunityStage(oppId, newStage);
      setOpportunities([...salesGtmEngine.getOpportunities()]);
      triggerNotification(`Opportunity stage updated to ${newStage}`);
    } catch (err: any) {
      triggerNotification(err.message);
    }
  };

  const handleCreateQuote = (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const newQ = salesGtmEngine.createQuote({
        accountId: quoteForm.accountId,
        opportunityId: quoteForm.opportunityId,
        currency: quoteForm.currency,
        lineItems: [
          {
            productId: 'prod_api_ent',
            productName: quoteForm.productName,
            sku: quoteForm.sku,
            quantity: quoteForm.quantity,
            unitPrice: quoteForm.unitPrice,
            discountType: quoteForm.discountType,
            discountValue: quoteForm.discountValue,
          },
        ],
        paymentTerms: quoteForm.paymentTerms,
      });
      setQuotes([...salesGtmEngine.getQuotes()]);
      setDealDeskRequests([...salesGtmEngine.getDealDeskRequests()]);
      setIsQuoteModalOpen(false);
      triggerNotification(`Quote ${newQ.quoteNumber} created ($${newQ.totalAmount.toLocaleString()}) - Status: ${newQ.status}`);
    } catch (err: any) {
      triggerNotification(err.message);
    }
  };

  const handleApproveDealDesk = (requestId: string, role: 'SalesManager' | 'RevOps' | 'Finance') => {
    const updated = salesGtmEngine.approveDealDeskRequest(requestId, role);
    setDealDeskRequests([...salesGtmEngine.getDealDeskRequests()]);
    triggerNotification(`Deal Desk Request approved by ${role}. Current Status: ${updated.status}`);
  };

  const selectedOpp = opportunities.find((o) => o.id === selectedOppId) || opportunities[0];
  const copilotSummary = selectedOpp ? salesGtmEngine.getAICopilotSummary(selectedOpp.id) : null;
  const selectedAccount360 = salesGtmEngine.getAccount360(selectedAccountId);

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#1E293B] font-sans pb-16">
      {/* Header Banner */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-20 shadow-2xs">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#0E7044] text-white flex items-center justify-center shadow-sm">
              <TrendingUp className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold tracking-wider text-[#0E7044] uppercase bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-200 flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  Security Gate 30.9: PASS
                </span>
                <span className="text-xs text-[#64748B] font-mono">Prompt 31 Global GTM</span>
              </div>
              <h1 className="text-xl font-black text-[#0F172A] tracking-tight">
                Chief Revenue Officer (CRO) Command Center & GTM OS
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* PaidMonetizationMode Switch Badge */}
            <div className="bg-slate-100 border border-slate-200 rounded-xl px-3 py-1.5 flex items-center gap-2">
              <span className="text-xs font-bold text-slate-600">PAID_MONETIZATION_MODE:</span>
              <span
                className={`text-xs font-black uppercase px-2 py-0.5 rounded-md ${
                  paidMonetizationMode === 'OFF'
                    ? 'bg-amber-100 text-amber-800 border border-amber-300'
                    : paidMonetizationMode === 'ON'
                    ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                    : 'bg-blue-100 text-blue-800 border border-blue-300'
                }`}
              >
                {paidMonetizationMode}
              </span>
              <button
                onClick={handleToggleMonetizationMode}
                className="text-xs font-semibold text-[#0E7044] hover:underline cursor-pointer ml-1"
              >
                Modifier
              </button>
            </div>

            <button
              onClick={() => setIsNewLeadModalOpen(true)}
              className="px-4 py-2 bg-[#0E7044] hover:bg-[#0B5C37] text-white rounded-xl text-xs font-bold transition-colors shadow-xs flex items-center gap-2 cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              <span>Nouveau Prospect (Lead)</span>
            </button>

            <button
              onClick={() => setIsQuoteModalOpen(true)}
              className="px-4 py-2 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-xl text-xs font-bold transition-colors shadow-xs flex items-center gap-2 cursor-pointer"
            >
              <FileText className="w-4 h-4" />
              <span>Nouveau Devis (Quote)</span>
            </button>
          </div>
        </div>

        {/* Global Navigation Sub-Tabs */}
        <div className="max-w-7xl mx-auto px-6 mt-2">
          <div className="flex border-b border-[#E2E8F0] gap-6 overflow-x-auto">
            {[
              { id: 'overview', label: 'Vue Exécutive (CRO)', icon: PieChart },
              { id: 'pipeline', label: `Pipeline Deals (${opportunities.length})`, icon: BarChart3 },
              { id: 'leads', label: `Prospects / Leads (${leads.length})`, icon: Users },
              { id: 'accounts', label: `Comptes & 360 (${accounts.length})`, icon: Building2 },
              { id: 'quotes', label: `Devis Commercial (${quotes.length})`, icon: FileText },
              { id: 'dealdesk', label: `Deal Desk Exception (${dealDeskRequests.filter((d) => d.status !== 'APPROVED').length})`, icon: Sliders },
              { id: 'forecast', label: 'Sales Forecast Engine', icon: TrendingUp },
              { id: 'partners', label: `Partenariats (${partners.length})`, icon: Globe },
              { id: 'expansion', label: `Expansion & Renewals (${expansionSignals.length + renewals.length})`, icon: Award },
              { id: 'copilot', label: 'IA Sales Copilot', icon: Sparkles },
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`pb-3 text-xs font-bold border-b-2 transition-colors flex items-center gap-2 shrink-0 cursor-pointer ${
                    activeTab === tab.id
                      ? 'border-[#0E7044] text-[#0E7044]'
                      : 'border-transparent text-[#64748B] hover:text-[#0F172A]'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </header>

      {/* Main Container */}
      <main className="max-w-7xl mx-auto px-6 mt-8">
        {notificationMsg && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 text-[#0E7044] rounded-xl text-xs font-bold flex items-center gap-3 animate-fade-in shadow-xs">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
            <span>{notificationMsg}</span>
          </div>
        )}

        {/* KPI Row (Executive Level) */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-white p-4 rounded-xl border border-[#E2E8F0] shadow-2xs">
            <span className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider block">Pipeline Total</span>
            <div className="text-xl font-black text-[#0F172A] mt-1">${(totalPipelineUsd / 1000).toFixed(0)}k USD</div>
            <span className="text-[10px] text-emerald-600 font-semibold flex items-center gap-1 mt-1">
              <ArrowUpRight className="w-3 h-3" /> +18.4% ce trimestre
            </span>
          </div>

          <div className="bg-white p-4 rounded-xl border border-[#E2E8F0] shadow-2xs">
            <span className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider block">Pipeline Pondéré</span>
            <div className="text-xl font-black text-[#0E7044] mt-1">${(weightedPipelineUsd / 1000).toFixed(0)}k USD</div>
            <span className="text-[10px] text-slate-500 font-medium mt-1 block">Facteur probabilité</span>
          </div>

          <div className="bg-white p-4 rounded-xl border border-[#E2E8F0] shadow-2xs">
            <span className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider block">Commit Commercial</span>
            <div className="text-xl font-black text-blue-700 mt-1">${(commitPipelineUsd / 1000).toFixed(0)}k USD</div>
            <span className="text-[10px] text-blue-600 font-semibold mt-1 block">Engagement signé/très fort</span>
          </div>

          <div className="bg-white p-4 rounded-xl border border-[#E2E8F0] shadow-2xs">
            <span className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider block">Taux de Conversion</span>
            <div className="text-xl font-black text-[#0F172A] mt-1">{winRatePct}%</div>
            <span className="text-[10px] text-emerald-600 font-semibold mt-1 block">Win Rate qualifié</span>
          </div>

          <div className="bg-white p-4 rounded-xl border border-[#E2E8F0] shadow-2xs">
            <span className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider block">Renouvellements ARR</span>
            <div className="text-xl font-black text-purple-700 mt-1">${(renewalPipelineUsd / 1000).toFixed(0)}k USD</div>
            <span className="text-[10px] text-purple-600 font-semibold mt-1 block">Portefeuille récurrent</span>
          </div>

          <div className="bg-white p-4 rounded-xl border border-[#E2E8F0] shadow-2xs">
            <span className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider block">Qualité CRM</span>
            <div className="text-xl font-black text-amber-600 mt-1">{dataQualityIssues.length} alertes</div>
            <span className="text-[10px] text-amber-700 font-medium mt-1 block">RevOps Audit Engine</span>
          </div>
        </div>

        {/* TAB 1: EXECUTIVE OVERVIEW (CRO) */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Pipeline Stage Distribution Chart / Summary */}
              <div className="lg:col-span-2 bg-white rounded-2xl border border-[#E2E8F0] p-6 shadow-2xs">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-base font-black text-[#0F172A]">Répartition du Pipeline par Étape Commerciale</h2>
                    <p className="text-xs text-[#64748B]">Visualisation de la valeur engagée par jalon de qualification</p>
                  </div>
                  <span className="text-xs font-mono font-bold text-[#0E7044] bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-200">
                    FX Planning: 1 EUR = 1.08 USD
                  </span>
                </div>

                <div className="space-y-4">
                  {[
                    { stage: 'DISCOVERY', label: 'Découverte (10%)', count: 2, value: 80000, color: 'bg-slate-400' },
                    { stage: 'QUALIFICATION', label: 'Qualification SQL (25%)', count: 3, value: 150000, color: 'bg-blue-400' },
                    { stage: 'TECHNICAL_VALIDATION', label: 'Validation Technique & APIs (50%)', count: 2, value: 210000, color: 'bg-indigo-500' },
                    { stage: 'SECURITY_COMPLIANCE', label: 'Sécurité & Conformité CISO (60%)', count: 1, value: 120000, color: 'bg-purple-500' },
                    { stage: 'NEGOTIATION', label: 'Négociation & Redlines (80%)', count: 1, value: 240000, color: 'bg-emerald-600' },
                    { stage: 'CLOSED_WON', label: 'Gagné (100%)', count: 2, value: 360000, color: 'bg-emerald-800' },
                  ].map((stg) => {
                    const pct = Math.min(100, Math.round((stg.value / 400000) * 100));
                    return (
                      <div key={stg.stage} className="space-y-1.5">
                        <div className="flex items-center justify-between text-xs font-bold text-[#0F172A]">
                          <span>{stg.label} ({stg.count} opportunités)</span>
                          <span className="font-mono">${stg.value.toLocaleString()} USD</span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
                          <div className={`h-full ${stg.color} transition-all duration-500`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* CRO Action Required Module */}
              <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 shadow-2xs">
                <div className="flex items-center gap-2 mb-4">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  <h2 className="text-base font-black text-[#0F172A]">Actions Urgentes CRO & RevOps</h2>
                </div>
                <div className="space-y-3">
                  {dealDeskRequests.filter((d) => d.status !== 'APPROVED').map((req) => (
                    <div key={req.id} className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs space-y-1">
                      <div className="flex items-center justify-between font-bold text-amber-900">
                        <span>Deal Desk: Remise {req.discountPercentage}%</span>
                        <span className="text-[10px] bg-amber-200 px-1.5 py-0.5 rounded font-mono">{req.status}</span>
                      </div>
                      <p className="text-amber-800 text-[11px]">{req.accountName} - {req.businessRationale}</p>
                      <button
                        onClick={() => setActiveTab('dealdesk')}
                        className="text-[11px] font-bold text-amber-900 hover:underline cursor-pointer pt-1 block"
                      >
                        Examiner au Deal Desk &rarr;
                      </button>
                    </div>
                  ))}

                  {dataQualityIssues.slice(0, 3).map((issue) => (
                    <div key={issue.id} className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs space-y-1">
                      <div className="flex items-center justify-between font-bold text-[#0F172A]">
                        <span>CRM Quality: {issue.issueType}</span>
                        <span className="text-[10px] bg-red-100 text-red-800 px-1.5 py-0.5 rounded font-mono">{issue.severity}</span>
                      </div>
                      <p className="text-slate-600 text-[11px]">{issue.entityName} - {issue.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 2: PIPELINE DEALS */}
        {activeTab === 'pipeline' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-white p-4 rounded-xl border border-[#E2E8F0]">
              <div className="flex items-center gap-3">
                <Search className="w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Rechercher une opportunité ou un compte..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 w-64 focus:outline-hidden focus:border-[#0E7044]"
                />
              </div>

              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-400" />
                <span className="text-xs font-bold text-slate-600">Étape:</span>
                <select
                  value={stageFilter}
                  onChange={(e) => setStageFilter(e.target.value)}
                  className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-hidden"
                >
                  <option value="ALL">Toutes les étapes</option>
                  <option value="DISCOVERY">DISCOVERY</option>
                  <option value="QUALIFICATION">QUALIFICATION</option>
                  <option value="TECHNICAL_VALIDATION">TECHNICAL_VALIDATION</option>
                  <option value="SECURITY_COMPLIANCE">SECURITY_COMPLIANCE</option>
                  <option value="NEGOTIATION">NEGOTIATION</option>
                  <option value="CLOSED_WON">CLOSED_WON</option>
                  <option value="CLOSED_LOST">CLOSED_LOST</option>
                </select>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden shadow-2xs">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-[#E2E8F0] text-[11px] font-bold text-[#64748B] uppercase tracking-wider">
                      <th className="py-3 px-4">Opportunité</th>
                      <th className="py-3 px-4">Compte Client</th>
                      <th className="py-3 px-4">Produit</th>
                      <th className="py-3 px-4">Étape (Stage)</th>
                      <th className="py-3 px-4">Valeur Est.</th>
                      <th className="py-3 px-4">Probabilité</th>
                      <th className="py-3 px-4">Sécurité / CISO</th>
                      <th className="py-3 px-4">Actions Stage</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E2E8F0] text-xs font-medium">
                    {opportunities
                      .filter((o) => stageFilter === 'ALL' || o.stage === stageFilter)
                      .filter((o) => o.name.toLowerCase().includes(searchFilter.toLowerCase()) || o.accountName.toLowerCase().includes(searchFilter.toLowerCase()))
                      .map((opp) => (
                        <tr key={opp.id} className="hover:bg-slate-50/80 transition-colors">
                          <td className="py-3.5 px-4 font-bold text-[#0F172A]">
                            <div>{opp.name}</div>
                            <span className="text-[10px] text-slate-500 font-normal">Fermeture visée: {opp.expectedCloseDate}</span>
                          </td>
                          <td className="py-3.5 px-4 text-slate-700 font-semibold">{opp.accountName}</td>
                          <td className="py-3.5 px-4 text-slate-600">
                            <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-[11px] font-mono">
                              {opp.productFamily}
                            </span>
                          </td>
                          <td className="py-3.5 px-4">
                            <span className="px-2.5 py-1 bg-emerald-50 text-[#0E7044] border border-emerald-200 rounded-md font-bold text-[10px] font-mono">
                              {opp.stage}
                            </span>
                          </td>
                          <td className="py-3.5 px-4 font-mono font-bold text-[#0F172A]">${opp.estimatedValue.toLocaleString()} {opp.currency}</td>
                          <td className="py-3.5 px-4 font-mono font-bold text-slate-700">{opp.probabilityPct}%</td>
                          <td className="py-3.5 px-4">
                            <span
                              className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                                opp.securityReviewStatus === 'APPROVED'
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : opp.securityReviewStatus === 'IN_REVIEW'
                                  ? 'bg-amber-100 text-amber-800'
                                  : 'bg-slate-100 text-slate-600'
                              }`}
                            >
                              {opp.securityReviewStatus}
                            </span>
                          </td>
                          <td className="py-3.5 px-4">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleUpdateOpportunityStage(opp.id, 'NEGOTIATION')}
                                className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded text-[10px] font-bold cursor-pointer"
                              >
                                Négociation
                              </button>
                              <button
                                onClick={() => handleUpdateOpportunityStage(opp.id, 'CLOSED_WON')}
                                className="px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[10px] font-bold cursor-pointer"
                              >
                                Won
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* TAB 3: PROSPECTS & LEADS */}
        {activeTab === 'leads' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between bg-white p-4 rounded-xl border border-[#E2E8F0]">
              <h2 className="text-base font-black text-[#0F172A]">Registre des Prospects Qualifiés (Leads)</h2>
              <button
                onClick={() => setIsNewLeadModalOpen(true)}
                className="px-4 py-2 bg-[#0E7044] hover:bg-[#0B5C37] text-white rounded-xl text-xs font-bold transition-colors cursor-pointer flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                <span>Nouveau Lead</span>
              </button>
            </div>

            <div className="bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden shadow-2xs">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-[#E2E8F0] text-[11px] font-bold text-[#64748B] uppercase tracking-wider">
                      <th className="py-3 px-4">Entreprise & Contact</th>
                      <th className="py-3 px-4">Source Provenance</th>
                      <th className="py-3 px-4">Marché</th>
                      <th className="py-3 px-4">Score Multi-Signaux</th>
                      <th className="py-3 px-4">Statut Lead</th>
                      <th className="py-3 px-4">Qualification</th>
                      <th className="py-3 px-4">Action Conversion</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E2E8F0] text-xs font-medium">
                    {leads.map((ld) => (
                      <tr key={ld.id} className="hover:bg-slate-50/80 transition-colors">
                        <td className="py-3.5 px-4">
                          <div className="font-bold text-[#0F172A]">{ld.companyName}</div>
                          <div className="text-slate-500 text-[11px]">{ld.contactName} ({ld.businessEmail})</div>
                        </td>
                        <td className="py-3.5 px-4 font-mono text-slate-600 text-[11px]">{ld.source}</td>
                        <td className="py-3.5 px-4 text-slate-700 font-bold">{ld.country} ({ld.market})</td>
                        <td className="py-3.5 px-4">
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-black text-[#0E7044] text-sm">{ld.score.totalScore}/100</span>
                            <div className="text-[9px] text-slate-400">
                              Fit:{ld.score.productFit} | Intent:{ld.score.commercialIntent}
                            </div>
                          </div>
                        </td>
                        <td className="py-3.5 px-4">
                          <span
                            className={`px-2 py-0.5 rounded font-bold text-[10px] ${
                              ld.status === 'QUALIFIED'
                                ? 'bg-emerald-100 text-emerald-800'
                                : ld.status === 'CONVERTED'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-slate-100 text-slate-700'
                            }`}
                          >
                            {ld.status}
                          </span>
                        </td>
                        <td className="py-3.5 px-4 font-mono font-bold text-slate-800">{ld.qualificationState}</td>
                        <td className="py-3.5 px-4">
                          {ld.status !== 'CONVERTED' ? (
                            <button
                              onClick={() => handleConvertLead(ld.id)}
                              className="px-3 py-1 bg-[#0E7044] hover:bg-[#0B5C37] text-white rounded-lg text-[10px] font-bold cursor-pointer transition-colors"
                            >
                              Convertir en Compte &rarr;
                            </button>
                          ) : (
                            <span className="text-emerald-700 font-bold text-[11px] flex items-center gap-1">
                              <CheckCircle2 className="w-3.5 h-3.5" /> Compte Créé
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* TAB 4: COMPTES & 360 */}
        {activeTab === 'accounts' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 shadow-2xs">
              <h2 className="text-base font-black text-[#0F172A] mb-4">Comptes Commerciaux</h2>
              <div className="space-y-3">
                {accounts.map((acc) => (
                  <button
                    key={acc.id}
                    onClick={() => setSelectedAccountId(acc.id)}
                    className={`w-full text-left p-4 rounded-xl border transition-all cursor-pointer ${
                      selectedAccountId === acc.id
                        ? 'border-[#0E7044] bg-emerald-50/50 shadow-xs'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-xs text-[#0F172A]">{acc.name}</span>
                      <span className="text-[10px] bg-slate-100 px-2 py-0.5 rounded font-mono text-slate-600 font-bold">
                        {acc.accountTier}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1">{acc.domain} | HQ: {acc.headquartersMarket}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Account 360 View */}
            <div className="lg:col-span-2 bg-white rounded-2xl border border-[#E2E8F0] p-6 shadow-2xs space-y-6">
              {selectedAccount360 ? (
                <>
                  <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                    <div>
                      <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-widest bg-emerald-50 px-2 py-0.5 rounded">
                        Account 360 Commercial View
                      </span>
                      <h2 className="text-xl font-black text-[#0F172A] mt-1">{selectedAccount360.account.name}</h2>
                      <p className="text-xs text-slate-500">
                        Secteur: {selectedAccount360.account.industry} | Tier: {selectedAccount360.account.accountTier}
                      </p>
                    </div>

                    <div className="text-right">
                      <span className="text-xs text-slate-500 block font-medium">ARR Estimé</span>
                      <span className="text-lg font-black font-mono text-[#0E7044]">
                        ${selectedAccount360.account.annualRevenueEstimateUsd?.toLocaleString()} USD
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                      <h3 className="text-xs font-bold text-[#0F172A] uppercase tracking-wider">Contacts d'Achat</h3>
                      {selectedAccount360.contacts.map((c) => (
                        <div key={c.id} className="text-xs space-y-0.5">
                          <div className="font-bold text-[#0F172A]">{c.name} ({c.jobTitle})</div>
                          <div className="text-[11px] text-slate-500">{c.businessEmail} | Rôle: {c.roleInBuyingProcess}</div>
                        </div>
                      ))}
                    </div>

                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                      <h3 className="text-xs font-bold text-[#0F172A] uppercase tracking-wider">Contrats Actifs</h3>
                      {selectedAccount360.contracts.length > 0 ? (
                        selectedAccount360.contracts.map((ctr) => (
                          <div key={ctr.id} className="text-xs space-y-0.5">
                            <div className="font-bold text-emerald-800">{ctr.documentReference}</div>
                            <div className="text-[11px] text-slate-500">Statut: {ctr.status} | Échéance: {ctr.expirationDate}</div>
                          </div>
                        ))
                      ) : (
                        <span className="text-xs text-slate-400 italic">Aucun contrat actif signé.</span>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center text-slate-400 text-xs py-12">Sélectionnez un compte pour ouvrir Account 360.</div>
              )}
            </div>
          </div>
        )}

        {/* TAB 5: DEVIS COMMERCIAL (QUOTES) */}
        {activeTab === 'quotes' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between bg-white p-4 rounded-xl border border-[#E2E8F0]">
              <h2 className="text-base font-black text-[#0F172A]">Registre des Devis Commerciaux (Quotes)</h2>
              <button
                onClick={() => setIsQuoteModalOpen(true)}
                className="px-4 py-2 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-xl text-xs font-bold transition-colors cursor-pointer flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                <span>Créer un Devis</span>
              </button>
            </div>

            <div className="bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden shadow-2xs">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-[#E2E8F0] text-[11px] font-bold text-[#64748B] uppercase tracking-wider">
                      <th className="py-3 px-4">N° Devis</th>
                      <th className="py-3 px-4">Montant Subtotal</th>
                      <th className="py-3 px-4">Remises Accordées</th>
                      <th className="py-3 px-4">Net Total</th>
                      <th className="py-3 px-4">Paiement</th>
                      <th className="py-3 px-4">Statut Approval</th>
                      <th className="py-3 px-4">Monetization Notice</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E2E8F0] text-xs font-medium">
                    {quotes.map((q) => (
                      <tr key={q.id} className="hover:bg-slate-50/80 transition-colors">
                        <td className="py-3.5 px-4 font-mono font-bold text-[#0F172A]">{q.quoteNumber} (v{q.version})</td>
                        <td className="py-3.5 px-4 font-mono text-slate-600">${q.subtotalAmount.toLocaleString()} {q.currency}</td>
                        <td className="py-3.5 px-4 font-mono text-amber-700 font-bold">-${q.totalDiscountAmount.toLocaleString()} USD</td>
                        <td className="py-3.5 px-4 font-mono font-black text-[#0E7044] text-sm">${q.totalAmount.toLocaleString()} USD</td>
                        <td className="py-3.5 px-4 text-slate-700 font-bold">{q.paymentTerms}</td>
                        <td className="py-3.5 px-4">
                          <span
                            className={`px-2 py-0.5 rounded font-bold text-[10px] ${
                              q.status === 'APPROVED' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                            }`}
                          >
                            {q.status}
                          </span>
                        </td>
                        <td className="py-3.5 px-4 text-[10px] text-slate-500 italic max-w-xs">{q.paidMonetizationNotice}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* TAB 6: DEAL DESK */}
        {activeTab === 'dealdesk' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
              <h2 className="text-base font-black text-[#0F172A] mb-2">Deal Desk Exceptions & Commercial Governance</h2>
              <p className="text-xs text-[#64748B]">
                Validation obligatoire des dérogations tarifaires, remises &gt; 15%, conditions de paiement ou SLAs sur-mesure.
              </p>
            </div>

            <div className="space-y-4">
              {dealDeskRequests.map((req) => (
                <div key={req.id} className="bg-white rounded-2xl border border-[#E2E8F0] p-6 shadow-2xs space-y-4">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
                    <div>
                      <span className="text-[10px] font-bold text-amber-800 uppercase bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                        {req.requestType}
                      </span>
                      <h3 className="text-base font-black text-[#0F172A] mt-1">{req.opportunityName}</h3>
                      <p className="text-xs text-slate-500">Demandeur: {req.requestedBy} | Compte: {req.accountName}</p>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-slate-600">Statut:</span>
                      <span className="px-3 py-1 bg-amber-100 text-amber-900 rounded-lg text-xs font-black font-mono">
                        {req.status}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                    <div className="bg-slate-50 p-3 rounded-xl border border-slate-200">
                      <span className="text-slate-500 text-[10px] uppercase font-bold block">Prix Catalogue</span>
                      <span className="font-mono font-bold text-slate-800">${req.listPriceUsd.toLocaleString()} USD</span>
                    </div>
                    <div className="bg-slate-50 p-3 rounded-xl border border-slate-200">
                      <span className="text-slate-500 text-[10px] uppercase font-bold block">Prix Proposé (Remise {req.discountPercentage}%)</span>
                      <span className="font-mono font-bold text-[#0E7044]">${req.proposedPriceUsd.toLocaleString()} USD</span>
                    </div>
                    <div className="bg-slate-50 p-3 rounded-xl border border-slate-200">
                      <span className="text-slate-500 text-[10px] uppercase font-bold block">TCV Est. (3 Ans)</span>
                      <span className="font-mono font-bold text-blue-800">${req.estimatedContractValueUsd.toLocaleString()} USD</span>
                    </div>
                    <div className="bg-slate-50 p-3 rounded-xl border border-slate-200">
                      <span className="text-slate-500 text-[10px] uppercase font-bold block">Marge Brute Est.</span>
                      <span className="font-mono font-bold text-emerald-800">{req.estimatedGrossMarginPct}%</span>
                    </div>
                  </div>

                  <div className="text-xs text-slate-700 bg-amber-50/50 p-3 rounded-xl border border-amber-100">
                    <span className="font-bold text-amber-900 block mb-1">Motif Business:</span>
                    {req.businessRationale}
                  </div>

                  <div className="flex items-center justify-between pt-2">
                    <div className="text-xs text-slate-500">
                      Approbateurs Requis: <span className="font-bold text-slate-700">{req.requiredApprovers.join(', ')}</span> |
                      Approuvé par: <span className="font-bold text-emerald-700">{req.approvedByList.length > 0 ? req.approvedByList.join(', ') : 'Aucun'}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      {!req.approvedByList.includes('SalesManager') && (
                        <button
                          onClick={() => handleApproveDealDesk(req.id, 'SalesManager')}
                          className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold cursor-pointer transition-colors"
                        >
                          Approuver (SalesManager)
                        </button>
                      )}
                      {!req.approvedByList.includes('RevOps') && (
                        <button
                          onClick={() => handleApproveDealDesk(req.id, 'RevOps')}
                          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold cursor-pointer transition-colors"
                        >
                          Approuver (RevOps)
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TAB 7: SALES FORECAST */}
        {activeTab === 'forecast' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
              <h2 className="text-base font-black text-[#0F172A] mb-2">Sales Forecast Engine & Accuracy Tracker</h2>
              <p className="text-xs text-[#64748B]">
                Prévisions de ventes par catégorie de commit, taux d'exactitude historique et suivi des biais d'estimation.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {forecastSnapshots.map((snap) => (
                <React.Fragment key={snap.id}>
                  <div className="bg-white p-5 rounded-2xl border border-[#E2E8F0] shadow-2xs space-y-2">
                    <span className="text-xs text-slate-500 font-bold uppercase">Pipeline Brut Total</span>
                    <div className="text-2xl font-black text-[#0F172A]">${(snap.totalPipelineValueUsd / 1000).toFixed(0)}k USD</div>
                    <span className="text-[11px] text-slate-500">Nombre d'opportunités: {snap.opportunitiesCount}</span>
                  </div>

                  <div className="bg-white p-5 rounded-2xl border border-[#E2E8F0] shadow-2xs space-y-2">
                    <span className="text-xs text-[#0E7044] font-bold uppercase">Pipeline Pondéré</span>
                    <div className="text-2xl font-black text-[#0E7044]">${(snap.weightedPipelineValueUsd / 1000).toFixed(0)}k USD</div>
                    <span className="text-[11px] text-slate-500">Ajusté selon probabilités</span>
                  </div>

                  <div className="bg-white p-5 rounded-2xl border border-[#E2E8F0] shadow-2xs space-y-2">
                    <span className="text-xs text-blue-700 font-bold uppercase">Commit Engagé</span>
                    <div className="text-2xl font-black text-blue-800">${(snap.commitValueUsd / 1000).toFixed(0)}k USD</div>
                    <span className="text-[11px] text-slate-500">Deals en phase finale</span>
                  </div>

                  <div className="bg-white p-5 rounded-2xl border border-[#E2E8F0] shadow-2xs space-y-2">
                    <span className="text-xs text-purple-700 font-bold uppercase">Précision Historique</span>
                    <div className="text-2xl font-black text-purple-900">{snap.forecastAccuracyPct}%</div>
                    <span className="text-[11px] text-emerald-600 font-bold">Biais: {snap.forecastBiasPct}% (Modéré)</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* TAB 8: PARTENARIATS (PARTNERS) */}
        {activeTab === 'partners' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-2xs">
              <h2 className="text-base font-black text-[#0F172A] mb-2">Partner Ecosystem & Distribution Channels</h2>
              <p className="text-xs text-[#64748B]">
                Gestion des partenaires d'intégration, apporteurs d'affaires, affiliés et accords technologiques stratégiques.
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden shadow-2xs">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-50 border-b border-[#E2E8F0] text-[11px] font-bold text-[#64748B] uppercase">
                    <th className="py-3 px-4">Partenaire</th>
                    <th className="py-3 px-4">Type Partenariat</th>
                    <th className="py-3 px-4">Marchés Couverts</th>
                    <th className="py-3 px-4">Modèle Commercial</th>
                    <th className="py-3 px-4">Statut Intégration</th>
                    <th className="py-3 px-4">Referrals Apportés</th>
                    <th className="py-3 px-4">Pipeline Généré</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]">
                  {partners.map((pt) => (
                    <tr key={pt.id}>
                      <td className="py-3.5 px-4 font-bold text-[#0F172A]">{pt.name}</td>
                      <td className="py-3.5 px-4 font-mono">{pt.type}</td>
                      <td className="py-3.5 px-4 text-slate-700">{pt.markets.join(', ')}</td>
                      <td className="py-3.5 px-4 font-bold text-slate-800">{pt.commercialModel}</td>
                      <td className="py-3.5 px-4 font-bold text-emerald-700">{pt.technicalIntegrationStatus}</td>
                      <td className="py-3.5 px-4 font-mono font-bold text-slate-900">{pt.referralsSourcedCount} leads</td>
                      <td className="py-3.5 px-4 font-mono font-bold text-[#0E7044]">${pt.pipelineGeneratedUsd.toLocaleString()} USD</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TAB 9: EXPANSION & RENEWALS */}
        {activeTab === 'expansion' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 shadow-2xs space-y-4">
              <h2 className="text-base font-black text-[#0F172A]">Signaux d'Expansion Client (Upsell)</h2>
              <div className="space-y-3">
                {expansionSignals.map((sig) => (
                  <div key={sig.id} className="p-4 bg-emerald-50/60 border border-emerald-200 rounded-xl text-xs space-y-1">
                    <div className="flex items-center justify-between font-bold text-[#0E7044]">
                      <span>Signal: {sig.signalType}</span>
                      <span className="text-[10px] bg-emerald-200 text-emerald-900 px-2 py-0.5 rounded font-mono">{sig.severity}</span>
                    </div>
                    <p className="text-slate-700">{sig.accountName} - {sig.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 shadow-2xs space-y-4">
              <h2 className="text-base font-black text-[#0F172A]">Portefeuille de Renouvellements (ARR)</h2>
              <div className="space-y-3">
                {renewals.map((ren) => (
                  <div key={ren.id} className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-xs space-y-1">
                    <div className="flex items-center justify-between font-bold text-[#0F172A]">
                      <span>{ren.accountName}</span>
                      <span className="font-mono font-bold text-purple-800">${ren.targetRenewalArrUsd.toLocaleString()} USD</span>
                    </div>
                    <p className="text-slate-500 text-[11px]">Échéance: {ren.renewalDate} | Statut Santé: {ren.healthStatus}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* TAB 10: IA SALES COPILOT */}
        {activeTab === 'copilot' && (
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-8 shadow-2xs max-w-4xl mx-auto space-y-6">
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-lg font-black text-[#0F172A]">IA Sales Copilot Engine (RAG & Deal Intelligence)</h2>
                <p className="text-xs text-slate-500">Analyse automatisée des risques d'opportunité et génération de brouillons d'e-mails</p>
              </div>
            </div>

            <div className="space-y-4">
              <label className="text-xs font-bold text-slate-700 block">Sélectionner une opportunité à analyser:</label>
              <select
                value={selectedOppId}
                onChange={(e) => setSelectedOppId(e.target.value)}
                className="w-full text-xs bg-slate-50 border border-slate-200 rounded-xl p-3 focus:outline-hidden font-bold"
              >
                {opportunities.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name} (${o.estimatedValue.toLocaleString()} USD - Stage: {o.stage})
                  </option>
                ))}
              </select>
            </div>

            {copilotSummary && (
              <div className="space-y-6 pt-2">
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-xs leading-relaxed space-y-2">
                  <span className="font-bold text-[#0F172A] block uppercase text-[10px] tracking-wider">Résumé Exécutif</span>
                  <p className="text-slate-700">{copilotSummary.executiveSummary}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-amber-50 rounded-xl border border-amber-200 text-xs space-y-2">
                    <span className="font-bold text-amber-900 block uppercase text-[10px] tracking-wider">Risques Détectés</span>
                    <ul className="list-disc list-inside space-y-1 text-amber-800">
                      {copilotSummary.keyRisks.map((rk, idx) => (
                        <li key={idx}>{rk}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200 text-xs space-y-2">
                    <span className="font-bold text-[#0E7044] block uppercase text-[10px] tracking-wider">Actions Recommandées</span>
                    <ul className="list-disc list-inside space-y-1 text-[#0E7044]">
                      {copilotSummary.recommendedNextActions.map((act, idx) => (
                        <li key={idx}>{act}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="p-4 bg-purple-50 rounded-xl border border-purple-200 text-xs space-y-2 font-mono">
                  <span className="font-bold text-purple-900 block uppercase text-[10px] tracking-wider">Brouillon Suivi E-mail Généré</span>
                  <pre className="whitespace-pre-wrap text-purple-950 text-[11px] leading-relaxed font-sans">{copilotSummary.suggestedFollowUpEmailDraft}</pre>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* MODAL 1: NEW LEAD */}
      {isNewLeadModalOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-2xl max-w-lg w-full p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="text-base font-black text-[#0F172A]">Enregistrer un Nouveau Prospect (Lead)</h3>
              <button onClick={() => setIsNewLeadModalOpen(false)} className="text-slate-400 hover:text-slate-600 cursor-pointer">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateLead} className="space-y-4 text-xs">
              <div>
                <label className="font-bold text-slate-700 block mb-1">Nom de l'Entreprise</label>
                <input
                  type="text"
                  required
                  placeholder="ex: BNP Paribas Corporate"
                  value={newLeadForm.companyName}
                  onChange={(e) => setNewLeadForm({ ...newLeadForm, companyName: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 focus:outline-hidden"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-bold text-slate-700 block mb-1">Nom du Contact</label>
                  <input
                    type="text"
                    required
                    placeholder="ex: Marc Vasseur"
                    value={newLeadForm.contactName}
                    onChange={(e) => setNewLeadForm({ ...newLeadForm, contactName: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 focus:outline-hidden"
                  />
                </div>
                <div>
                  <label className="font-bold text-slate-700 block mb-1">E-mail Professionnel</label>
                  <input
                    type="email"
                    required
                    placeholder="m.vasseur@bnp.fr"
                    value={newLeadForm.businessEmail}
                    onChange={(e) => setNewLeadForm({ ...newLeadForm, businessEmail: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 focus:outline-hidden"
                  />
                </div>
              </div>

              <div>
                <label className="font-bold text-slate-700 block mb-1">Intérêt Produit</label>
                <input
                  type="text"
                  value={newLeadForm.productInterest}
                  onChange={(e) => setNewLeadForm({ ...newLeadForm, productInterest: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 focus:outline-hidden"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsNewLeadModalOpen(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold cursor-pointer"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#0E7044] hover:bg-[#0B5C37] text-white rounded-xl font-bold cursor-pointer"
                >
                  Enregistrer & Scorer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: NEW QUOTE */}
      {isQuoteModalOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-2xl max-w-lg w-full p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="text-base font-black text-[#0F172A]">Générer un Devis Commercial Governé</h3>
              <button onClick={() => setIsQuoteModalOpen(false)} className="text-slate-400 hover:text-slate-600 cursor-pointer">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateQuote} className="space-y-4 text-xs">
              <div>
                <label className="font-bold text-slate-700 block mb-1">Produit de la Proposition</label>
                <input
                  type="text"
                  value={quoteForm.productName}
                  onChange={(e) => setQuoteForm({ ...quoteForm, productName: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 focus:outline-hidden"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-bold text-slate-700 block mb-1">Quantité (Mois / Unités)</label>
                  <input
                    type="number"
                    value={quoteForm.quantity}
                    onChange={(e) => setQuoteForm({ ...quoteForm, quantity: Number(e.target.value) })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 focus:outline-hidden font-mono"
                  />
                </div>
                <div>
                  <label className="font-bold text-slate-700 block mb-1">Prix Unitaire (USD)</label>
                  <input
                    type="number"
                    value={quoteForm.unitPrice}
                    onChange={(e) => setQuoteForm({ ...quoteForm, unitPrice: Number(e.target.value) })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 focus:outline-hidden font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="font-bold text-slate-700 block mb-1">Remise Accordée (%)</label>
                <input
                  type="number"
                  value={quoteForm.discountValue}
                  onChange={(e) => setQuoteForm({ ...quoteForm, discountValue: Number(e.target.value) })}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 focus:outline-hidden font-mono font-bold text-amber-800"
                />
                <span className="text-[10px] text-slate-500 mt-1 block">
                  Remise &gt; 15% déclenchera automatiquement une demande Deal Desk.
                </span>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsQuoteModalOpen(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold cursor-pointer"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-xl font-bold cursor-pointer"
                >
                  Générer Devis
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
